import React, { useMemo, useState } from "react";
import { Box as MuiBox, Typography, Slider, IconButton } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { CurrencyFilter } from "./CurrencyFilter";
import { Box, Tooltip } from "@/components/atoms";
import {
  useFilterJobs,
  useUserContext,
  MIN_SALARY,
  MAX_SALARY,
} from "@/providers/contexts";
import { useJobPostFilterOptions } from "@/hooks";
import { debounce } from "lodash";

// Only available to premium users

export const SalaryRangeFilter = () => {
  const { disabledBasicContent, disabledProContent, isAuthenticated } =
    useUserContext();
  const { filters: jobPostFilters } = useJobPostFilterOptions();
  const { filters, dispatch } = useFilterJobs();

  const {
    selectedSalaryRangeMin,
    selectedSalaryRangeMax,
    selectedJobSalaryCurrencies,
  } = filters;

  const disabledContent =
    disabledBasicContent || disabledProContent || !isAuthenticated;

  const currencySymbols = React.useMemo(() => {
    const jobSalaryCurrencies = jobPostFilters?.jobSalaryCurrencies ?? [];

    if (!jobSalaryCurrencies) return { USD: "$" };
    return jobSalaryCurrencies.reduce(
      (acc, { currency_code, currency_sign }) => {
        acc[currency_code] = currency_sign;
        return acc;
      },
      {} as { [key: string]: string },
    );
  }, [jobPostFilters?.jobSalaryCurrencies]);

  const defaultRange = [MIN_SALARY, MAX_SALARY];
  const [localRange, setLocalRange] = useState<number[]>([
    selectedSalaryRangeMin ?? MIN_SALARY,
    selectedSalaryRangeMax ?? MAX_SALARY,
  ]);

  const debouncedDispatch = useMemo(
    () =>
      debounce((range: number[]) => {
        dispatch({
          type: "SET_FILTERS",
          payload: {
            selectedSalaryRangeMin: range[0],
            selectedSalaryRangeMax: range[1],
          },
        });
      }, 300),
    [dispatch],
  );

  const handleSliderChange = (_: any, newValue: number | number[]) => {
    if (Array.isArray(newValue)) {
      setLocalRange(newValue);
      debouncedDispatch(newValue);
    }
  };

  const clearSalaryFilter = () => {
    setLocalRange(defaultRange);
    dispatch({
      type: "SET_FILTERS",
      payload: {
        selectedSalaryRangeMin: MIN_SALARY,
        selectedSalaryRangeMax: MAX_SALARY,
      },
    });
  };

  const selectedCurrencyCodes =
    selectedJobSalaryCurrencies?.map(c => c.currency_code) || [];

  const currencySymbolsList = selectedCurrencyCodes
    .map(code => currencySymbols[code] || code)
    .join(", ");

  const disableClear =
    disabledContent ||
    (localRange[0] === MIN_SALARY && localRange[1] === MAX_SALARY);

  const contents = (
    <>
      <MuiBox
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography
          variant="subtitle1"
          sx={{ opacity: disabledContent ? 0.5 : 1 }}
        >
          Salary
        </Typography>
        <IconButton
          onClick={clearSalaryFilter}
          size="small"
          disabled={disableClear}
          sx={{
            cursor: disableClear ? "not-allowed" : "pointer",
            opacity: disableClear ? 0.5 : 1,
          }}
        >
          <ClearIcon fontSize="small" />
        </IconButton>
      </MuiBox>
      <Typography
        variant="subtitle2"
        sx={{ opacity: disabledContent ? 0.5 : 1 }}
      >
        {currencySymbolsList}
        {localRange[0]} - {currencySymbolsList}
        {localRange[1]}
      </Typography>
      <Slider
        value={localRange}
        onChange={handleSliderChange}
        valueLabelDisplay="auto"
        min={MIN_SALARY}
        max={MAX_SALARY}
        sx={{ marginBottom: 2 }}
        disabled={disabledContent}
      />
      <CurrencyFilter />
    </>
  );

  return (
    <Box
      componentName={`salary-container`}
      sx={{
        mt: 2,
        padding: 2,
      }}
    >
      {disabledContent ? (
        <Tooltip
          componentName={`salary-container-tooltip`}
          tooltipMessage={"Upgrade your account to access this feature"}
          readMoreLink="/memberships"
        >
          {contents}
        </Tooltip>
      ) : (
        contents
      )}
    </Box>
  );
};
